import React from "react";
import { Dialog } from "@/components/Dialog/Dialog";
import { Button } from "@/components/ui/button";

interface UnsavedChangesDialogProps {
  isOpen: boolean;
  onCancel: () => void;
  onDiscard: () => void;
  onSaveAndContinue: () => void;
  isSaving?: boolean;
  title?: string;
  description?: string;
  saveButtonText?: string;
  discardButtonText?: string;
  cancelButtonText?: string;
  // Optional: container to render dialog into (e.g., editor wrapper)
  container?: Element | null;
}

const UnsavedChangesDialog: React.FC<UnsavedChangesDialogProps> = ({
  isOpen,
  onCancel,
  onDiscard,
  onSaveAndContinue,
  isSaving = false,
  title = "Leave page?",
  description = `You have unsaved changes. Select <span class='font-medium text-brand-primary-800'>"Save & Continue"</span> to keep your changes, or <span class='font-medium text-brand-primary-800'>"Discard Changes"</span> to exit and discard it.`,
  saveButtonText = "Save & Continue",
  discardButtonText = "Discard Changes",
  cancelButtonText = "Cancel",
  container,
}) => {
  return (
    <Dialog
      isOpen={isOpen}
      onClose={onCancel}
      container={container}
      className="max-w-[512px] p-6 rounded-xl flex flex-col items-start gap-4 border border-[#E7E9EF] bg-white shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
    >
      <div className="flex flex-col">
        {/* Title */}
        <h2 className="text-[16px] font-medium leading-[28px] text-brand-primary-800">
          {title}
        </h2>

        {/* Description */}
        <p
          className="text-[13px] text-brand-primary-600 leading-[20px]"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </div>

      {/* Action Buttons */}
      <div className="w-full flex items-center justify-between">
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={isSaving}
          className="font-medium"
        >
          {cancelButtonText}
        </Button>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={onDiscard}
            disabled={isSaving}
            className="font-medium"
          >
            {discardButtonText}
          </Button>

          <Button
            variant="brand"
            onClick={onSaveAndContinue}
            disabled={isSaving}
            className="font-medium"
          >
            {isSaving ? "Saving..." : saveButtonText}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default UnsavedChangesDialog;
